import * as React from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import { Capacitor } from '@capacitor/core';

const LoadingBar = ({ isLocationLoading, locationPermission, locationError }) => {
  const [ loading, setLoading ] = React.useState(true);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, []);

  // Handle location permission denied case
  const handleOpenSettings = () => {
    if (Capacitor.isNativePlatform()) {
      // Open device settings for location permissions
      window.location.href = 'app-settings:';
    } else {
      // For web, show instructions
      alert('Please enable location access in your browser settings and refresh the page.');
    }
  };

  return (
    <div id="loadingScreen" style={{
      position:'fixed',
      top:0,
      left:0,
      height:'100vh',
      width:'100vw',
      zIndex:'4',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      overflow: 'hidden'
    }} 
    className={loading ? 'true' : 'false'}>
      {/* Background Video */}
      <video
        autoPlay
        muted
        loop
        playsInline
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          minWidth: '100%',
          minHeight: '100%',
          width: 'auto',
          height: 'auto',
          transform: 'translate(-50%, -50%)',
          zIndex: 1,
          objectFit: 'cover'
        }}
      >
        <source src={process.env.PUBLIC_URL + '/bg-video.mp4'} type="video/mp4" />
      </video>
      
      {/* Dark Overlay */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 2
      }} />
      
      {/* Content */}
      <div style={{
        position: 'relative',
        zIndex: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        textAlign: 'center',
        width: '100%',
        maxWidth: '400px',
        padding: '0 20px'
      }}>
        <img
          src={process.env.PUBLIC_URL + '/logo.jpeg'}
          alt="Delta Power Group Logo"
          style={{
            width: '250px',
            height: 'auto',
            marginBottom: 16
          }}
        />
        <Typography 
          variant="h4" 
          sx={{ 
            color: '#fff', 
            fontWeight: 'bold',
            marginBottom: 16,
            textAlign: 'center',
            letterSpacing: '2px'
          }}
        >
          Delta Ops
        </Typography>
        
        {/* Show different content based on location state */}
        {locationPermission === 'denied' ? (
          <>
            <Typography 
              variant="h6" 
              sx={{ 
                color: '#fff', 
                marginBottom: 2,
                textAlign: 'center'
              }}
            >
              Location Access Required
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.8)', 
                marginBottom: 3,
                textAlign: 'center',
                lineHeight: 1.5
              }}
            >
              Delta Ops needs location access to center the map on your position and help you navigate to prospects on the map effectively.
            </Typography>
            <Button
              variant="contained"
              onClick={handleOpenSettings}
              sx={{
                backgroundColor: '#222',
                color: '#fff',
                borderRadius: '24px',
                padding: '12px 24px',
                fontWeight: 700,
                fontSize: '1rem',
                letterSpacing: 1,
                transition: 'background 0.2s',
                marginBottom: 2,
                boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
                '&:hover': {
                  backgroundColor: '#333',
                },
              }}
            >
              Open Settings
            </Button>
            <Typography 
              variant="caption" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.6)', 
                textAlign: 'center',
                display: 'block'
              }}
            >
              Enable location access and restart the app
            </Typography>
          </>
        ) : isLocationLoading ? (
          <>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)', 
                marginBottom: 2,
                textAlign: 'center'
              }}
            >
              Setting up your field operations...
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.7)', 
                marginBottom: 3,
                textAlign: 'center'
              }}
            >
              Getting your location for territory management
            </Typography>
            <Box sx={{ width: '100%' }}>
              <LinearProgress 
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#ffffff',
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          </>
        ) : (
          <>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'rgba(255, 255, 255, 0.9)', 
                marginBottom: 2,
                textAlign: 'center'
              }}
            >
              Finding your location...
            </Typography>
            <Box sx={{ width: '100%' }}>
              <LinearProgress 
                sx={{
                  height: 8,
                  borderRadius: 4,
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#ffffff',
                    borderRadius: 4,
                  },
                }}
              />
            </Box>
          </>
        )}
      </div>
    </div>
  );
};

export default LoadingBar;


